import type { ElementType, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { Check, Minus, Plus, ZoomIn } from 'lucide-react'

import { cn } from '@/lib/utils'
import { gsap, registerMotion, prefersReducedMotion } from '@/lib/motion'
import { useBag } from '@/components/commerce/bag-context'
import { ProductLightbox } from '@/components/commerce/product-lightbox'
import {
    productDetails,
    productGallery,
    productName,
    productSummary,
    productVariants
} from '@/constants/product'
import { siteConfig } from '@/constants/site'

const formatPrice = (value: number): string => `$${value}`

interface ProductPanelProps {
    /** `h1` on the shop page, `h2` when the panel sits inside the home page. */
    headingAs?: ElementType
    /** Eager-load the hero shot when the panel is above the fold. */
    priority?: boolean
}

/**
 * The buy experience: gallery with lightbox, size, quantity, details and add to bag.
 * Shared by the home order section and the dedicated shop page so there is one
 * implementation of the purchase flow.
 */
export const ProductPanel = ({
    headingAs: Heading = 'h2',
    priority = false
}: ProductPanelProps): ReactNode => {
    const { addLine } = useBag()

    const [variantId, setVariantId] = useState(productVariants[0].id)
    const [quantity, setQuantity] = useState(1)
    const [imageIndex, setImageIndex] = useState(0)
    const [openDetail, setOpenDetail] = useState<string | null>(productDetails[0].id)
    const [justAdded, setJustAdded] = useState(false)
    const [lightboxOpen, setLightboxOpen] = useState(false)

    const mainImage = useRef<HTMLDivElement | null>(null)
    const priceRef = useRef<HTMLParagraphElement | null>(null)

    const variant = productVariants.find((entry) => entry.id === variantId) ?? productVariants[0]

    // Crossfade the hero shot whenever a different thumbnail is chosen.
    useEffect(() => {
        const node = mainImage.current

        if (!node) return

        registerMotion()

        if (prefersReducedMotion()) return

        const tween = gsap.fromTo(
            node,
            { opacity: 0, scale: 0.97 },
            { opacity: 1, scale: 1, duration: 0.65, ease: 'nuvea', overwrite: true }
        )

        return () => {
            tween.kill()
        }
    }, [imageIndex])

    // Nudge the price when the size changes so the swap is legible.
    useEffect(() => {
        const node = priceRef.current

        if (!node) return

        registerMotion()

        if (prefersReducedMotion()) return

        const tween = gsap.fromTo(
            node,
            { y: 10, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, ease: 'nuvea', overwrite: true }
        )

        return () => {
            tween.kill()
        }
    }, [variantId])

    useEffect(() => {
        if (!justAdded) return

        const timer = window.setTimeout(() => setJustAdded(false), 2200)

        return () => window.clearTimeout(timer)
    }, [justAdded])

    const onAdd = (): void => {
        addLine(variantId, quantity)
        setJustAdded(true)
        setQuantity(1)
    }

    return (
        <>
            <div className='grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16'>
                <div data-order-gallery='' className='flex flex-col gap-4'>
                    <button
                        type='button'
                        data-order-main=''
                        onClick={() => setLightboxOpen(true)}
                        aria-label='Open the full product image viewer'
                        className='group relative aspect-square w-full cursor-zoom-in overflow-hidden rounded-2xl bg-cream'
                    >
                        <div ref={mainImage} className='absolute inset-0'>
                            <Image
                                src={productGallery[imageIndex].src}
                                alt={productGallery[imageIndex].alt}
                                fill
                                priority={priority && imageIndex === 0}
                                sizes='(min-width: 1024px) 50vw, 100vw'
                                className={cn(
                                    'will-change-transform',
                                    productGallery[imageIndex].fit === 'cover'
                                        ? 'object-cover'
                                        : 'object-contain p-6 md:p-12'
                                )}
                            />
                        </div>

                        <span className='type-micro absolute right-4 bottom-4 flex items-center gap-1.5 rounded-full bg-cream/90 px-3 py-2 text-ink opacity-0 backdrop-blur-sm transition-opacity duration-400 ease-nuvea group-hover:opacity-100 group-focus-visible:opacity-100'>
                            <ZoomIn className='size-3.5' strokeWidth={1.6} aria-hidden='true' />
                            View
                        </span>
                    </button>

                    <ul className='grid grid-cols-5 gap-2 md:gap-3'>
                        {productGallery.map((image, index) => (
                            <li key={image.src}>
                                <button
                                    type='button'
                                    onClick={() => setImageIndex(index)}
                                    aria-label={`Show image ${index + 1} of ${productGallery.length}`}
                                    aria-current={index === imageIndex ? 'true' : undefined}
                                    className={cn(
                                        'relative aspect-square w-full cursor-pointer overflow-hidden rounded-xl bg-cream transition-[border-color,transform] duration-400 ease-nuvea',
                                        'border hover:-translate-y-0.5',
                                        index === imageIndex ? 'border-ink' : 'border-transparent hover:border-stone-line'
                                    )}
                                >
                                    <Image
                                        src={image.src}
                                        alt=''
                                        aria-hidden='true'
                                        fill
                                        sizes='120px'
                                        className={cn(
                                            image.fit === 'cover' ? 'object-cover' : 'object-contain p-2'
                                        )}
                                    />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='flex flex-col'>
                    <Heading data-order-detail='' className='type-h1 text-ink'>
                        {productName}

                        <span className='align-top text-[0.32em] leading-none'>
                            &trade;
                        </span>
                    </Heading>

                    <p
                        ref={priceRef}
                        data-order-detail=''
                        className='mt-4 flex items-baseline gap-3'
                    >
                        <span className='type-h3 text-ink tabular-nums'>
                            {formatPrice(variant.price)}
                        </span>

                        <span className='type-body text-slate-mute line-through tabular-nums'>
                            {formatPrice(variant.compareAt)}
                        </span>

                        <span className='type-micro rounded-full bg-sage px-3 py-1.5 text-ink'>
                            Save {formatPrice(variant.compareAt - variant.price)}
                        </span>
                    </p>

                    <fieldset data-order-detail='' className='mt-8'>
                        <legend className='type-label text-slate-mute'>
                            Size
                        </legend>

                        <div className='mt-3 flex flex-wrap gap-3'>
                            {productVariants.map((entry) => (
                                <button
                                    key={entry.id}
                                    type='button'
                                    onClick={() => setVariantId(entry.id)}
                                    aria-pressed={entry.id === variantId}
                                    className={cn(
                                        'type-small cursor-pointer rounded-full border px-5 py-3 transition-[background-color,border-color,color,transform] duration-400 ease-nuvea active:scale-[0.97]',
                                        entry.id === variantId
                                            ? 'border-ink bg-ink text-cream'
                                            : 'border-stone-line bg-cream text-ink hover:border-ink'
                                    )}
                                >
                                    {entry.label} ({entry.volume})
                                </button>
                            ))}
                        </div>
                    </fieldset>

                    <p data-order-detail='' className='type-copy mt-8 max-w-[56ch] text-slate-soft'>
                        {productSummary}
                    </p>

                    <div data-order-detail='' className='mt-8 border-t border-stone-line'>
                        {productDetails.map((detail) => {
                            const isOpen = openDetail === detail.id

                            return (
                                <div key={detail.id} className='border-b border-stone-line'>
                                    <h3>
                                        <button
                                            type='button'
                                            onClick={() => setOpenDetail(isOpen ? null : detail.id)}
                                            aria-expanded={isOpen}
                                            aria-controls={`detail-${detail.id}`}
                                            className='group flex w-full cursor-pointer items-center justify-between gap-4 py-4 text-left'
                                        >
                                            <span className='type-small text-ink'>
                                                {detail.title}
                                            </span>

                                            <span
                                                aria-hidden='true'
                                                className={cn(
                                                    'flex size-6 shrink-0 items-center justify-center rounded-full border border-stone-line text-ink transition-[transform,border-color] duration-500 ease-nuvea group-hover:border-ink',
                                                    isOpen && 'rotate-[135deg]'
                                                )}
                                            >
                                                <Plus className='size-3' strokeWidth={1.8} />
                                            </span>
                                        </button>
                                    </h3>

                                    <div
                                        id={`detail-${detail.id}`}
                                        className={cn(
                                            'grid transition-[grid-template-rows,opacity] duration-500 ease-nuvea',
                                            isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                                        )}
                                    >
                                        <div className='overflow-hidden'>
                                            <p className='type-copy max-w-[56ch] pb-5 text-slate-soft'>
                                                {detail.body}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div data-order-detail='' className='mt-8 flex flex-wrap items-center gap-3'>
                        <div className='flex items-center gap-1 rounded-full border border-stone-line bg-cream px-2 py-1.5'>
                            <button
                                type='button'
                                onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                                aria-label='Decrease quantity'
                                className='flex size-9 cursor-pointer items-center justify-center rounded-full text-ink transition-colors duration-300 hover:bg-sage'
                            >
                                <Minus className='size-4' strokeWidth={1.6} />
                            </button>

                            <span
                                className='type-small w-8 text-center text-ink tabular-nums'
                                aria-live='polite'
                            >
                                {quantity}
                            </span>

                            <button
                                type='button'
                                onClick={() => setQuantity((value) => Math.min(20, value + 1))}
                                aria-label='Increase quantity'
                                className='flex size-9 cursor-pointer items-center justify-center rounded-full text-ink transition-colors duration-300 hover:bg-sage'
                            >
                                <Plus className='size-4' strokeWidth={1.6} />
                            </button>
                        </div>

                        <button
                            type='button'
                            onClick={onAdd}
                            className={cn(
                                'type-small relative flex-1 cursor-pointer overflow-hidden rounded-full px-8 py-4 text-center',
                                'transition-[background-color,color,transform] duration-400 ease-nuvea active:scale-[0.99]',
                                justAdded ? 'bg-sage text-ink' : 'bg-ink text-cream hover:bg-ink/88'
                            )}
                        >
                            <span className='flex items-center justify-center gap-2'>
                                {justAdded ? (
                                    <>
                                        <Check className='size-4' strokeWidth={2} aria-hidden='true' />
                                        Added to bag
                                    </>
                                ) : (
                                    <>
                                        Add to Bag
                                        <span className='text-cream/60'>
                                            &mdash; {formatPrice(variant.price * quantity)}
                                        </span>
                                    </>
                                )}
                            </span>
                        </button>
                    </div>

                    <p data-order-detail='' className='type-micro mt-4 text-slate-mute'>
                        Free worldwide shipping. Made in {siteConfig.madeIn}. Thirty days to change your mind.
                    </p>
                </div>
            </div>

            <ProductLightbox
                images={productGallery}
                index={imageIndex}
                isOpen={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                onIndexChange={setImageIndex}
            />
        </>
    )
}
